// DiskAssessmentEditor.js — paired-row per-disk editor.
//
// For each disk renders a CURRENT row (read-only, original values) above
// an EDITING row (form controls). Editor can compare each cell directly.
//
// Aggressive cascade reset: changing the antibiotic clears potency, zone,
// and mode — the editor must re-confirm every dependent value. Prevents
// "lazy default acceptance" failure mode in clinical settings.
//
// No add/remove disks for v1 — those changes require a new plate image.

import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  Box,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import { getPotenciesForAntibiotic } from "./editCascade";

// Light grey for the CURRENT (read-only) row backgrounds.
const CURRENT_BG = "rgba(0, 0, 0, 0.025)";
// Highlight when an editable cell has been changed from its current value.
const CHANGED_BG = "rgba(255, 167, 38, 0.08)";

/**
 * Per-cell change flags relative to the original disk values.
 */
function diffOf(disk, originalDisk) {
  return {
    ab:
      (disk.antibioticId || disk.ab) !==
      (originalDisk?.antibioticId || originalDisk?.ab),
    potency: (disk.potency || "") !== (originalDisk?.potency || ""),
    zone:
      Number(disk.diameterMm ?? -1) !== Number(originalDisk?.diameterMm ?? -1),
    mode: (disk.mode || "M") !== (originalDisk?.mode || "M"),
  };
}

/**
 * True if a disk row has all required fields. Aggregated by EditTest's
 * Preview/Save validation across all disks.
 */
export function diskRowComplete(disk) {
  if (!disk) return false;
  if (!disk.antibioticId && !disk.ab) return false;
  if (!disk.potency || !String(disk.potency).trim()) return false;
  const mode = disk.mode || "M";
  if (mode === "NZ") return true;
  const z = Number(disk.diameterMm);
  if (!Number.isFinite(z) || z <= 0) return false;
  return true;
}

/**
 * One CURRENT row + one EDITING row for a single disk.
 */
function DiskPair({
  index,
  disk,
  originalDisk,
  allAntibiotics,
  allBreakpoints,
  onChange,
}) {
  const { t } = useTranslation();

  const selectedAntibiotic = useMemo(
    () =>
      allAntibiotics.find(
        (a) => a.whonetAbxCode === (disk.antibioticId || disk.ab),
      ) || null,
    [allAntibiotics, disk.antibioticId, disk.ab],
  );

  const availablePotencies = useMemo(
    () =>
      getPotenciesForAntibiotic(disk.antibioticId || disk.ab, allAntibiotics),
    [disk.antibioticId, disk.ab, allAntibiotics],
  );

  const changed = diffOf(disk, originalDisk);

  // Antibiotic change clears potency, zone, and mode. Editor must re-confirm.
  const handleAntibioticChange = (newAb) => {
    if (!newAb) {
      onChange(index, {
        ...disk,
        antibioticId: "",
        ab: "",
        antibioticName: "",
        antibioticSymbol: "",
        potency: "",
        diameterMm: null,
        mode: "M",
      });
      return;
    }
    onChange(index, {
      ...disk,
      antibioticId: newAb.whonetAbxCode,
      ab: newAb.whonetAbxCode,
      antibioticName: newAb.antibiotic,
      antibioticSymbol: newAb.whonetAbxCode,
      potency: "",
      diameterMm: null,
      mode: "M",
    });
  };

  const handlePotencyChange = (newPotency) => {
    onChange(index, { ...disk, potency: newPotency });
  };

  const handleZoneChange = (val) => {
    const num = val === "" ? null : Number(val);
    onChange(index, { ...disk, diameterMm: num });
  };

  const handleModeChange = (newMode) => {
    if (!newMode) return;
    if (newMode === "NZ") {
      onChange(index, { ...disk, mode: "NZ", diameterMm: null });
    } else {
      onChange(index, { ...disk, mode: newMode });
    }
  };

  const editCellSx = (isChanged) => ({
    verticalAlign: "middle",
    bgcolor: isChanged ? CHANGED_BG : "transparent",
  });

  const currentCellSx = {
    bgcolor: CURRENT_BG,
    fontSize: "0.95rem",
    color: "text.secondary",
    borderBottom: "none",
  };

  return (
    <>
      {/* CURRENT row */}
      <TableRow>
        <TableCell
          rowSpan={2}
          sx={{
            verticalAlign: "middle",
            bgcolor: CURRENT_BG,
            fontWeight: "bold",
          }}
        >
          #{index + 1}
        </TableCell>
        <TableCell sx={{ ...currentCellSx, fontStyle: "italic" }}>
          {t("diskEditor.current")}
        </TableCell>
        <TableCell sx={currentCellSx}>
          {originalDisk?.antibioticName || originalDisk?.ab || "—"}
        </TableCell>
        <TableCell sx={currentCellSx}>{originalDisk?.potency || "—"}</TableCell>
        <TableCell sx={currentCellSx}>
          {originalDisk?.mode === "NZ"
            ? t("diskEditor.noZone")
            : originalDisk?.diameterMm != null
              ? `${originalDisk.diameterMm} mm`
              : "—"}
        </TableCell>
        <TableCell sx={currentCellSx}>{originalDisk?.mode || "M"}</TableCell>
      </TableRow>

      {/* EDITING row */}
      <TableRow>
        <TableCell
          sx={{
            fontStyle: "italic",
            color: "primary.main",
            fontWeight: "bold",
          }}
        >
          {t("diskEditor.editing")}
        </TableCell>
        <TableCell sx={editCellSx(changed.ab)}>
          <Autocomplete
            size="small"
            options={allAntibiotics}
            getOptionLabel={(opt) => opt.antibiotic || ""}
            value={selectedAntibiotic}
            onChange={(_e, newVal) => handleAntibioticChange(newVal)}
            isOptionEqualToValue={(opt, val) =>
              opt.whonetAbxCode === val?.whonetAbxCode
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t("diskEditor.placeholderDrug")}
                variant="standard"
              />
            )}
            sx={{ minWidth: 200 }}
          />
        </TableCell>
        <TableCell sx={editCellSx(changed.potency)}>
          <FormControl size="small" sx={{ minWidth: 90 }} variant="standard">
            <Select
              value={
                availablePotencies.includes(disk.potency || "")
                  ? disk.potency
                  : ""
              }
              onChange={(e) => handlePotencyChange(e.target.value)}
              displayEmpty
              disabled={availablePotencies.length === 0}
            >
              <MenuItem value="" disabled>
                <em>
                  {availablePotencies.length === 0
                    ? "—"
                    : t("diskEditor.selectPotency")}
                </em>
              </MenuItem>
              {availablePotencies.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </TableCell>
        <TableCell sx={editCellSx(changed.zone)}>
          {disk.mode === "NZ" ? (
            <Typography variant="body2" color="text.secondary">
              {t("diskEditor.noZone")}
            </Typography>
          ) : (
            <TextField
              type="number"
              size="small"
              variant="standard"
              value={disk.diameterMm ?? ""}
              onChange={(e) => handleZoneChange(e.target.value)}
              placeholder={t("diskEditor.placeholderMm")}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {t("diskEditor.placeholderMm")}
                    </InputAdornment>
                  ),
                },
                htmlInput: { min: 6, max: 60, step: 1 },
              }}
              sx={{ width: 110 }}
            />
          )}
        </TableCell>
        <TableCell sx={editCellSx(changed.mode)}>
          <ToggleButtonGroup
            value={disk.mode || "M"}
            exclusive
            size="small"
            onChange={(_e, newMode) => handleModeChange(newMode)}
          >
            <ToggleButton value="M">M</ToggleButton>
            <ToggleButton value="NZ">NZ</ToggleButton>
          </ToggleButtonGroup>
        </TableCell>
      </TableRow>
    </>
  );
}

/**
 * Top-level disk editor — paired CURRENT/EDITING rows.
 */
export default function DiskAssessmentEditor({
  disks,
  originalDisks,
  allAntibiotics,
  allBreakpoints,
  onChange,
}) {
  const { t } = useTranslation();

  if (!Array.isArray(disks) || disks.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        {t("diskEditor.noDisksRecorded")}
      </Typography>
    );
  }

  return (
    <Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "grey.100" }}>
              <TableCell sx={{ fontWeight: "bold" }}>#</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("diskEditor.tableState")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("diskEditor.tableDrug")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("diskEditor.tablePotency")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("diskEditor.tableZone")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("diskEditor.tableMode")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {disks.map((d, i) => (
              <DiskPair
                key={i}
                index={i}
                disk={d}
                originalDisk={originalDisks?.[i]}
                allAntibiotics={allAntibiotics}
                allBreakpoints={allBreakpoints}
                onChange={onChange}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1.5 }}
      >
        {t("diskEditor.footnote")}
      </Typography>
    </Box>
  );
}
